import type { ReactElement } from 'react';
import React, { useRef, useState } from 'react';
import type { TooltipProps } from 'reactstrap';
import { Tooltip } from 'reactstrap';
import { v4 as uuid } from 'uuid';

type Props = {
  tooltip: string;
  children: ReactElement | ReactElement[];
  id?: string;
  tooltipProps?: Omit<TooltipProps, 'target' | 'toggle' | 'isOpen'>;
  className?: string;
};

export default function TooltipItem({
  tooltip,
  children,
  tooltipProps,
  className
}: React.PropsWithChildren<Props>): ReactElement {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { current: id } = useRef(`tooltip-${uuid()}`);

  return (
    <>
      <span id={id} className={className} style={{ height: 'fit-content' }}>
        {children}
      </span>
      <Tooltip
        placement="top"
        isOpen={tooltipOpen}
        target={id}
        toggle={(): void => setTooltipOpen((prev) => !prev)}
        boundariesElement="window"
        {...tooltipProps}
      >
        {tooltip}
      </Tooltip>
    </>
  );
}

/**
 * Only show a tooltip if the showTooltip flag is true, otherwise render the children without
 * wrapping it/them in a tooltip item.
 */
export function ConditionalTooltipItem({
  showTooltip,
  className,
  tooltip,
  children,
  id,
  tooltipProps
}: { showTooltip: boolean } & React.PropsWithChildren<Props>): ReactElement {
  if (showTooltip)
    return (
      <TooltipItem className={className} tooltip={tooltip} id={id} tooltipProps={tooltipProps}>
        {children}
      </TooltipItem>
    );
  return <>{children}</>;
}
